import React, { useState, useEffect, useRef } from "react";
import { C } from "../../../constants/design";
import type { DuelSetup, FormationId } from "../types/leagueDuel";
import { FORMATIONS, DEFAULT_FORMATION } from "../config/formations";
import { DEFAULT_SPELL_IDS } from "../config/spells";
import { MAX_ENERGY } from "../engine/energy";
import type { EnergyState } from "../engine/energy";
import type { DailyChestState } from "../engine/dailyChest";
import { rankProgress } from "../engine/duelRanking";
import { DuelSpellSetup } from "./DuelSpellSetup";
import {
  DUEL_ROTATION_ENABLED,
  DEFAULT_ATTACK_ROTATION,
  DEFAULT_DEFENSE_ROTATION,
  normalizeRotation,
  duelRotationSlots,
} from "../engine/duelRotationCombat";
import { PVE_SPELLS } from "../../stadium-trial/content/pveSpells";
import { FONT, ST_BEVEL, ST_STYLE } from "../../stadium-trial/components/StadiumTrialScreen";
import { makeHomeTeam } from "../data/mockTeams";
import { BOX, PP_IMGS, LAYER_IMGS } from "../../../constants/items";
import type { EquipmentStats } from "../engine/combat";
import { sb } from "../../../lib/supabase";
import { useI18n } from "../../../i18n/I18nContext";

// ─── Prep screen — layout v2 (no spells) ─────────────────────────────────────

const DONUT_SIZE   = 64;
const DONUT_STROKE = 6;
const R = (DONUT_SIZE - DONUT_STROKE) / 2;
const CIRC = 2 * Math.PI * R;

// ── Donut ring card ───────────────────────────────────────────────────────────
function DonutCard({
  pct,
  ringColor,
  centerContent,
  title,
  subtitle,
  onClick,
  pulse,
}: {
  pct: number;
  ringColor: string;
  centerContent: React.ReactNode;
  title: string;
  subtitle: string;
  onClick?: () => void;
  pulse?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      className={pulse ? "duel-lunge" : undefined}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: "linear-gradient(180deg,#121a2b,#0c1322)",
        border: `1px solid ${pulse ? ringColor : C.line}`,
        borderRadius: 16,
        clipPath: ST_BEVEL,
        padding: "14px 12px",
        cursor: onClick ? "pointer" : "default",
        boxShadow: pulse ? `0 0 14px ${ringColor}44` : undefined,
        transition: "border-color .2s, box-shadow .2s",
      }}
    >
      {/* Donut SVG */}
      <div style={{ position: "relative", width: DONUT_SIZE, height: DONUT_SIZE, flexShrink: 0 }}>
        <svg width={DONUT_SIZE} height={DONUT_SIZE} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={DONUT_SIZE / 2} cy={DONUT_SIZE / 2} r={R}
            fill="none" stroke={C.line} strokeWidth={DONUT_STROKE} />
          <circle
            cx={DONUT_SIZE / 2} cy={DONUT_SIZE / 2} r={R}
            fill="none" stroke={ringColor} strokeWidth={DONUT_STROKE} strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - Math.min(1, pct))}
            style={{ transition: "stroke-dashoffset .5s ease-out" }}
          />
        </svg>
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%,-50%)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {centerContent}
        </div>
      </div>

      {/* Labels */}
      <div style={{ minWidth: 0 }}>
        <div style={{ font: `700 14px ${FONT.display}`, letterSpacing: 0.5, color: C.text, whiteSpace: "nowrap" }}>{title}</div>
        <div style={{ font: `600 11px ${FONT.label}`, letterSpacing: 0.5, color: C.muted, marginTop: 2, whiteSpace: "nowrap" }}>{subtitle}</div>
      </div>
    </div>
  );
}

// ── Energy bar ────────────────────────────────────────────────────────────────
function formatCountdown(ms: number): string {
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function EnergyBar({ energy }: { energy: EnergyState }) {
  const { t } = useI18n();
  const targetRef = useRef(Date.now() + energy.msToNext);
  const [msLeft, setMsLeft] = useState(energy.msToNext);

  useEffect(() => {
    targetRef.current = Date.now() + energy.msToNext;
    setMsLeft(energy.msToNext);
  }, [energy.energy, energy.msToNext]);

  useEffect(() => {
    if (energy.isFull) return;
    const iv = setInterval(() => {
      const remaining = Math.max(0, targetRef.current - Date.now());
      setMsLeft(remaining);
    }, 1000);
    return () => clearInterval(iv);
  }, [energy.isFull, energy.energy]);

  const full = energy.energy >= MAX_ENERGY;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: C.muted, fontWeight: 700, marginBottom: 6 }}>
        <span style={{ color: C.gold, fontWeight: 900 }}>{energy.energy}/{MAX_ENERGY}</span>
        <span style={{ marginLeft: 4 }}>⚡</span>
        {!full && (
          <span style={{ color: C.faint, fontWeight: 500 }}>
            {" "}· {t("leagueDuel.energyNextIn", formatCountdown(msLeft))}
          </span>
        )}
        {full && <span style={{ color: C.turf, fontWeight: 600 }}> · {t("leagueDuel.energyFull")}</span>}
      </div>
      <style>{`
        @keyframes energy-breathe {
          0%, 100% { background-color: var(--pill-base); }
          50%       { background-color: var(--pill-light); }
        }
      `}</style>
      <div style={{ display: "flex", gap: 4 }}>
        {Array.from({ length: MAX_ENERGY }).map((_, i) => {
          const isRecharging = !full && i === energy.energy;
          return (
            <div
              key={i}
              style={{
                flex: 1, height: 5, borderRadius: 3,
                background: i < energy.energy ? C.gold : C.line,
                ...(isRecharging && {
                  ["--pill-base" as string]: C.line,
                  ["--pill-light" as string]: "rgba(255,255,255,0.45)",
                  animation: "energy-breathe 2.5s ease-in-out infinite",
                }),
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Defenses panel ────────────────────────────────────────────────────────────
interface DefenseRecord {
  attacker_name: string;
  attacker_avatar: string | null;
  result: "win" | "loss" | "draw";
  home_score: number;
  away_score: number;
  def_rep_delta: number;
  played_at: string;
}

// Suivi "vu" pour le point rouge de notification sur le bouton Defenses.
const DEFENSES_SEEN_KEY = "duel_defenses_seen_at";

function getDefensesSeenAt(): number {
  try {
    const v = Number(localStorage.getItem(DEFENSES_SEEN_KEY));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

function markDefensesSeen(): void {
  try {
    localStorage.setItem(DEFENSES_SEEN_KEY, String(Date.now()));
  } catch {}
  // Prévient les autres écrans (Lobby) que le point de notif doit disparaître.
  try {
    window.dispatchEvent(new Event("duel-defenses-seen"));
  } catch {}
}

/** Filtre PostgREST : par profile_id si dispo, sinon par nom. */
function defenderFilter(playerName: string, playerProfileId?: string | null): string {
  return playerProfileId
    ? `defender_profile_id=eq.${playerProfileId}`
    : `defender_name=eq.${encodeURIComponent(playerName)}`;
}

/** Résout l'URL de la photo de profil depuis la clé d'avatar stockée. */
function resolveAvatarUrl(avatar: string | null): string | undefined {
  if (!avatar) return undefined;
  return PP_IMGS[avatar] ?? LAYER_IMGS[avatar] ?? undefined;
}

function timeAgo(iso: string, t: (key: string, ...args: any[]) => string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return t("leagueDuel.timeJustNow");
  if (m < 60) return t("leagueDuel.timeMinutesAgo", m);
  const h = Math.floor(m / 60);
  if (h < 24) return t("leagueDuel.timeHoursAgo", h);
  return t("leagueDuel.timeDaysAgo", Math.floor(h / 24));
}

function DefensesPanel({
  playerName,
  playerProfileId,
  onClose,
}: {
  playerName: string;
  playerProfileId?: string | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [records, setRecords] = useState<DefenseRecord[] | null>(null);

  useEffect(() => {
    const filter = defenderFilter(playerName, playerProfileId);
    sb.get(
      `duel_history?${filter}&select=attacker_name,attacker_avatar,result,home_score,away_score,def_rep_delta,played_at&order=played_at.desc&limit=20`
    ).then(setRecords);
    // Ouvrir le panneau = tout marquer comme vu (efface le point rouge).
    markDefensesSeen();
  }, [playerProfileId, playerName]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      background: "rgba(10,7,8,0.80)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ padding: "20px 16px", maxWidth: 480, margin: "0 auto", width: "100%", flex: 1, overflowY: "auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: C.text, fontSize: 22, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}
          >
            ‹
          </button>
          <h2 style={{ fontWeight: 900, fontSize: 22, margin: 0 }}>{t("leagueDuel.defensesTitle")}</h2>
          <div style={{ fontSize: 12, color: C.muted, marginLeft: 4, marginTop: 3 }}>{t("leagueDuel.defensesSubtitle")}</div>
        </div>

        {records === null && (
          <div style={{ textAlign: "center", color: C.muted, padding: "40px 0" }}>{t("leagueDuel.defensesLoading")}</div>
        )}

        {records !== null && records.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🛡️</div>
            <div style={{ fontWeight: 800, color: C.text, marginBottom: 6 }}>{t("leagueDuel.defensesEmpty")}</div>
            <div style={{ fontSize: 13, color: C.muted }}>{t("leagueDuel.defensesEmptyDesc")}</div>
          </div>
        )}

        {records !== null && records.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {records.map((r, i) => {
              const weWon = r.result === "loss";
              const repDelta = r.def_rep_delta ?? 0;
              return (
                <div
                  key={i}
                  style={{
                    background: C.surface,
                    border: `1px solid ${weWon ? C.turf : "#c0392b44"}`,
                    borderRadius: 12, padding: "12px 14px",
                    display: "flex", alignItems: "center", gap: 12,
                  }}
                >
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, overflow: "hidden",
                      background: weWon ? "rgba(56,217,169,0.15)" : "rgba(192,57,43,0.15)",
                      border: `1.5px solid ${weWon ? C.turf : "#c0392b66"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 18,
                    }}>
                      {resolveAvatarUrl(r.attacker_avatar) ? (
                        <img src={resolveAvatarUrl(r.attacker_avatar)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span>👤</span>
                      )}
                    </div>
                    {/* Badge victoire/défaite en overlay */}
                    <div style={{
                      position: "absolute", bottom: -4, right: -4,
                      width: 18, height: 18, borderRadius: "50%",
                      background: weWon ? C.turf : "#e74c3c",
                      border: `2px solid ${C.surface}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9,
                    }}>
                      {weWon ? "🛡️" : "💥"}
                    </div>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.attacker_name}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      {timeAgo(r.played_at, t)}
                    </div>
                  </div>

                  <div style={{ textAlign: "center", flexShrink: 0 }}>
                    <div style={{
                      fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase",
                      color: weWon ? C.turf : "#e74c3c",
                      background: weWon ? "rgba(45,227,107,0.12)" : "rgba(231,76,60,0.12)",
                      borderRadius: 6, padding: "3px 8px",
                    }}>
                      {weWon ? t("leagueDuel.defenseWon") : t("leagueDuel.defenseLost")}
                    </div>
                  </div>

                  <div style={{
                    flexShrink: 0, textAlign: "right", minWidth: 42,
                    fontSize: 13, fontWeight: 800,
                    color: repDelta > 0 ? C.turf : repDelta < 0 ? "#e74c3c" : C.faint,
                  }}>
                    {repDelta > 0 ? `+${repDelta}` : repDelta === 0 ? "±0" : `${repDelta}`}
                    <div style={{ fontSize: 9, fontWeight: 600, color: C.muted }}>ELO</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Squad roster ──────────────────────────────────────────────────────────────
function AvatarCircle({
  size = 36,
  imageUrl,
  emoji,
}: {
  size?: number;
  imageUrl?: string;
  emoji?: string;
}) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "rgba(255,255,255,0.12)",
      border: `1.5px solid rgba(255,255,255,0.18)`,
      flexShrink: 0,
      overflow: "hidden",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {imageUrl ? (
        <img src={imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span style={{ fontSize: size * 0.48, lineHeight: 1 }}>{emoji ?? "👤"}</span>
      )}
    </div>
  );
}

function PlayerRow({
  name, isCaptain, power, avatarImageUrl, avatarEmoji,
}: {
  name: string;
  isCaptain?: boolean;
  power?: number;
  avatarImageUrl?: string;
  avatarEmoji?: string;
}) {
  const { t } = useI18n();
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: C.surface, border: `1px solid ${C.line}`,
      borderRadius: 10, padding: "10px 12px",
    }}>
      <AvatarCircle imageUrl={avatarImageUrl} emoji={avatarEmoji} />
      <div style={{ flex: 1, fontWeight: 700, fontSize: 13, color: C.text }}>{name}</div>
      {isCaptain && (
        <span style={{
          fontSize: 10, fontWeight: 800, color: C.text,
          background: "rgba(255,255,255,0.12)", borderRadius: 6,
          padding: "2px 7px", marginRight: 6,
        }}>
          {t("leagueDuel.captainBadge")}
        </span>
      )}
      {power !== undefined && (
        <span style={{ fontSize: 12, fontWeight: 800, color: C.gold, whiteSpace: "nowrap" }}>
          {power} ⚡
        </span>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function LeagueDuelPrep({
  onStart,
  onExit,
  playerName = "Me",
  playerProfileId,
  playerReputation = 0,
  playerAvatar,
  playerStats,
  playerLevel = 1,
  spellAttack,
  spellDefense,
  onSaveSpells,
  energy,
  chest,
  onClaimChest,
  launchStatus = "idle",
}: {
  onStart: (setup: DuelSetup) => void;
  onExit?: () => void;
  playerName?: string;
  playerProfileId?: string | null;
  playerReputation?: number;
  playerAvatar?: string;
  playerStats?: EquipmentStats;
  /** Niveau du joueur — déverrouillage des sorts (rotation 1v1). */
  playerLevel?: number;
  /** Rotations sauvegardées (players.equipped_spells) — undefined = défauts. */
  spellAttack?: string[];
  spellDefense?: string[];
  onSaveSpells?: (attack: string[], defense: string[]) => void;
  energy?: EnergyState;
  chest?: DailyChestState;
  onClaimChest?: () => void;
  launchStatus?: "idle" | "spending_energy" | "finding_opponent" | "creating_match";
}) {
  const { t } = useI18n();
  const [formation, setFormation] = useState<FormationId>(DEFAULT_FORMATION);
  const [showDefenses, setShowDefenses] = useState(false);
  const [showSpellSetup, setShowSpellSetup] = useState(false);
  const [hasNewDefenses, setHasNewDefenses] = useState(false);

  // Point rouge : y a-t-il une attaque plus récente que la dernière consultation ?
  useEffect(() => {
    let cancelled = false;
    const filter = defenderFilter(playerName, playerProfileId);
    sb.get(`duel_history?${filter}&select=played_at&order=played_at.desc&limit=1`)
      .then((rows) => {
        if (cancelled || !rows?.length) return;
        const latest = new Date(rows[0].played_at).getTime();
        setHasNewDefenses(latest > getDefensesSeenAt());
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [playerName, playerProfileId]);

  const openDefenses = () => {
    markDefensesSeen();
    setHasNewDefenses(false);
    setShowDefenses(true);
  };

  const prog        = rankProgress(playerReputation);
  const hasEnergy   = !energy || energy.energy > 0;
  const isLaunching = launchStatus !== "idle";
  const canStart    = hasEnergy && !isLaunching;
  const totalPower  = playerStats
    ? playerStats.technique + playerStats.defense + playerStats.physique + playerStats.vitesse
    : 0;

  const squad = makeHomeTeam(playerName);
  const [captain, ...teammates] = squad.players;

  const captainImageUrl = playerAvatar
    ? (PP_IMGS[playerAvatar] ?? LAYER_IMGS[playerAvatar] ?? undefined)
    : undefined;

  useEffect(() => {
    Object.values(BOX).forEach((url) => { const img = new Image(); img.src = url; });
  }, []);

  const launchLabel = (() => {
    if (launchStatus === "finding_opponent") return t("leagueDuel.findingOpponent");
    if (launchStatus === "creating_match")   return t("leagueDuel.creatingMatch");
    if (!hasEnergy) return t("leagueDuel.noEnergy");
    return t("leagueDuel.findOpponent");
  })();

  const chestPct   = chest ? chest.matches / chest.goal : 0;
  const chestReady = chest?.ready ?? false;
  const chestLabel = chest
    ? chest.claimed
      ? t("leagueDuel.chestClaimedShort")
      : `${chest.matches} / ${chest.goal} matches`
    : "—";

  const rankPct = prog.progressPercent;

  return (
    <div style={{ padding: "20px 16px 90px", maxWidth: 480, margin: "0 auto", color: C.text, fontFamily: FONT.ui }}>
      <style>{ST_STYLE}</style>
      <style>{`@keyframes duelNotifPulse{0%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(255,138,30,0.55)}50%{transform:scale(1.18);box-shadow:0 0 8px 3px rgba(255,138,30,0.35)}}.duelNotifDot{animation:duelNotifPulse 1.5s ease-in-out infinite}`}</style>

      {showDefenses && (
        <DefensesPanel playerName={playerName} playerProfileId={playerProfileId} onClose={() => setShowDefenses(false)} />
      )}
      {showSpellSetup && (
        <DuelSpellSetup
          playerLevel={playerLevel}
          playerPhysique={playerStats?.physique ?? 10}
          attack={spellAttack}
          defense={spellDefense}
          onSave={(a, d) => onSaveSpells?.(a, d)}
          onClose={() => setShowSpellSetup(false)}
        />
      )}

      {/* Header — centered title with an absolute boxed back button (left) and the
          Defenses button (right), exactly like the Camp screen's title bar. */}
      <div style={{ position: "relative", textAlign: "center", marginBottom: 20, animation: "stBanner 0.45s cubic-bezier(0.34,1.56,0.64,1) both" }}>
        {onExit && (
          <button
            onClick={onExit}
            style={{
              position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)",
              width: 38, height: 38, display: "grid", placeItems: "center",
              border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)",
              color: C.text, borderRadius: 10, fontSize: 16, cursor: "pointer",
            }}
          >
            ←
          </button>
        )}
        <div style={{ font: `700 24px ${FONT.display}`, letterSpacing: 2, textTransform: "uppercase", color: C.text }}>{t("leagueDuel.prepScreenTitle")}</div>
        {/* Wrapper non clippé : le point de notif est un frère du bouton, sinon
            le clipPath ST_BEVEL du bouton le coupe. */}
        <div style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}>
          <button
            onClick={openDefenses}
            style={{
              display: "block",
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${C.line}`,
              clipPath: ST_BEVEL,
              color: C.text,
              font: `700 11px ${FONT.label}`,
              letterSpacing: 1,
              textTransform: "uppercase",
              cursor: "pointer",
              padding: "7px 13px",
              whiteSpace: "nowrap",
            }}
          >
            {t("leagueDuel.defensesButton")}
          </button>
          {hasNewDefenses && (
            <span className="duelNotifDot" style={{
              position: "absolute", top: -4, right: -4,
              width: 12, height: 12, borderRadius: "50%",
              background: "#FF8A1E", border: `2px solid ${C.bg ?? "#0a0708"}`,
              pointerEvents: "none",
            }} />
          )}
        </div>
      </div>

      {/* Two donut cards */}
      <div style={{ display: "flex", gap: 10, marginBottom: 22 }}>
        {/* Daily Chest */}
        <DonutCard
          pct={chestPct}
          ringColor={chestReady ? C.gold : C.turf}
          pulse={chestReady}
          onClick={chestReady ? onClaimChest : undefined}
          centerContent={
            <img
              src={BOX.premium}
              alt=""
              style={{ width: 42, height: 42, objectFit: "contain", opacity: chest?.claimed ? 0.35 : 1 }}
            />
          }
          title={t("leagueDuel.chestTitle")}
          subtitle={chestLabel}
        />

        {/* Rank */}
        <DonutCard
          pct={rankPct}
          ringColor={C.gold}
          centerContent={
            <span style={{ fontSize: 24, lineHeight: 1 }}>{prog.rankIcon}</span>
          }
          title={prog.rank}
          subtitle={
            prog.nextMinRep != null
              ? `${prog.progressInCurrentRank} / ${prog.progressNeeded} RP`
              : t("leagueDuel.maxRank")
          }
        />
      </div>

      {/* Your fighter — SOLO (rotation 1v1: the 5-player squad is gone) */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, font: `700 11px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: C.muted, marginBottom: 2 }}>
          <span style={{ width: 5, height: 12, background: C.turf, borderRadius: 1, display: "inline-block" }} />{t("leagueDuel.squadTitle")}
        </div>

        <div style={{ marginBottom: 6, marginTop: 10 }}>
          <PlayerRow
            name={playerName}
            isCaptain
            power={totalPower}
            avatarImageUrl={captainImageUrl}
            avatarEmoji={captainImageUrl ? undefined : "👤"}
          />
        </div>

        {!DUEL_ROTATION_ENABLED && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {teammates.map((p) => (
              <PlayerRow key={p.id} name={p.name} power={0} avatarEmoji={p.avatar} />
            ))}
          </div>
        )}
      </div>

      {/* Spell rotations — attack & defense (rotation 1v1) */}
      {DUEL_ROTATION_ENABLED && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, font: `700 11px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: C.muted, marginBottom: 12 }}>
            <span style={{ width: 5, height: 12, background: "#4CC9F0", borderRadius: 1, display: "inline-block" }} />Spell Rotations
          </div>
          <button
            onClick={() => setShowSpellSetup(true)}
            style={{
              width: "100%", textAlign: "left", cursor: "pointer",
              background: "linear-gradient(180deg,#121a2b,#0c1322)", border: `1.5px solid ${C.line}`, borderRadius: 16, clipPath: ST_BEVEL, padding: "12px 14px",
              display: "flex", flexDirection: "column", gap: 8, color: C.text,
            }}
          >
            {([["⚔️", normalizeRotation(spellAttack, DEFAULT_ATTACK_ROTATION, duelRotationSlots(playerLevel))],
               ["🛡️", normalizeRotation(spellDefense, DEFAULT_DEFENSE_ROTATION, duelRotationSlots(playerLevel))]] as const).map(([icon, rot], i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, flex: "none" }}>{icon}</span>
                <div style={{ display: "flex", gap: 5 }}>
                  {rot.map((id, j) => {
                    const col = PVE_SPELLS[id]?.color ?? C.line;
                    return (
                      <span key={j} style={{
                        width: 30, height: 30, display: "grid", placeItems: "center", fontSize: 15,
                        borderRadius: 8, background: `${col}1c`, border: `1px solid ${col}66`, boxShadow: `0 0 10px ${col}44`,
                      }}>{PVE_SPELLS[id]?.icon}</span>
                    );
                  })}
                </div>
                <span style={{ marginLeft: "auto", font: `700 11px ${FONT.label}`, letterSpacing: 1, color: C.faint }}>{i === 0 ? "EDIT ›" : ""}</span>
              </div>
            ))}
          </button>
        </div>
      )}

      {/* Formation — football sim only, hidden in rotation mode */}
      {!DUEL_ROTATION_ENABLED && (
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, font: `700 11px ${FONT.label}`, letterSpacing: 2, textTransform: "uppercase", color: C.muted, marginBottom: 12 }}>
          <span style={{ width: 5, height: 12, background: C.gold, borderRadius: 1, display: "inline-block" }} />{t("leagueDuel.formationTitle")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {FORMATIONS.map((f) => {
            const active = formation === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFormation(f.id)}
                style={{
                  padding: "16px 0",
                  borderRadius: 12,
                  border: `1.5px solid ${active ? C.gold : C.line}`,
                  background: active ? "rgba(245,179,1,0.10)" : C.surface,
                  color: C.text,
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 900, fontSize: 18, color: active ? C.gold : C.text }}>{f.label}</div>
                <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>{t(`leagueDuel.formations.${f.id}`)}</div>
              </button>
            );
          })}
        </div>
      </div>
      )}

      <div style={{ flex: 1 }} />

      {/* Energy */}
      {energy && <EnergyBar energy={energy} />}

      {/* Launch button */}
      <button
        onClick={() => { if (canStart) onStart({ formation, spellIds: DEFAULT_SPELL_IDS }); }}
        disabled={!canStart}
        style={{
          width: "100%",
          padding: "16px 0",
          borderRadius: 14,
          clipPath: ST_BEVEL,
          border: "none",
          background: canStart ? "linear-gradient(135deg,#FF7A2C,#FF2E44)" : "rgba(255,255,255,0.06)",
          color: canStart ? "#fff" : C.muted,
          font: `800 17px ${FONT.display}`,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          animation: canStart ? "stCtaGlow 2.6s ease-in-out infinite" : undefined,
          cursor: canStart ? "pointer" : "not-allowed",
        }}
      >
        {launchLabel}
      </button>
    </div>
  );
}
